import { AlertTriangleIcon } from '@repo/studio-design-system-ui-icons/alerts-feedback/alert-triangle'
import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import * as React from 'react'
import { lazyComponent } from 'utils/lazy-component'
import { create } from 'zustand'
import { nextFrame } from 'utils/dom'
import { useHydrated } from 'hooks/use-hydrated'
import { ErrorBoundary } from 'react-error-boundary'
import { createPortal } from 'react-dom'
import { Button } from '@repo/studio-design-system-ui-button'
import { tw } from 'utils/classnames'

const sonner = () => import('sonner')
const SonnerToaster = lazyComponent(() => sonner().then(m => m.Toaster))

/**
 * Only keeps track of whether the Toaster has been called.
 * So if we have never called `toast`, then we don't import `sonner`.
 */
const useToastStore = create(() => ({
  hasBeenCalled: process.env.NODE_ENV === 'test',
}))

/**
 * Promise that resolves when the Sonner React component is fully mounted and ready.
 * This is necessary for React 19 compatibility, where effect timing has changed.
 *
 * Without this, there's a race condition where:
 * 1. The Sonner module is preloaded
 * 2. The Toaster portal is rendered
 * 3. But the <SonnerToaster.component> isn't fully mounted yet
 * 4. Calling sonner.custom() before the component is ready results in toasts not appearing
 *
 * The fix: wait for SonnerComponentWrapper to mount before allowing toasts to be created.
 *
 * The promise is reset when Sonner unmounts so tests (and remounts) wait for a fresh mount
 * instead of reusing a resolver from a previous Toaster instance.
 */
function createToasterReadyState() {
  let resolve: (value: boolean) => void = () => {}
  // oxlint-disable-next-line promise/avoid-new
  const promise = new Promise<boolean>(res => {
    resolve = res
  })
  return { promise, resolve }
}

let toasterReady = createToasterReadyState()
let sonnerPreloaded = false

export function Toaster({ className, ...props }: React.ComponentProps<'div'>) {
  const hasBeenCalled = useToastStore(state => Boolean(state.hasBeenCalled))
  const hydrated = useHydrated()

  if (!hasBeenCalled) {
    return null
  }

  if (!hydrated) {
    return null
  }

  return createPortal(
    <div
      data-testid="toaster"
      className={tw(
        'z-studio-toaster pointer-events-none absolute isolate h-full w-full',
        className,
      )}
      {...props}
    >
      <ErrorBoundary FallbackComponent={() => null}>
        <React.Suspense fallback={null}>
          <SonnerComponentWrapper />
        </React.Suspense>
      </ErrorBoundary>
    </div>,
    document.body,
  )
}

function SonnerComponentWrapper() {
  React.useEffect(() => {
    toasterReady.resolve(true)

    return () => {
      toasterReady = createToasterReadyState()
    }
  }, [])

  return (
    // oxlint-disable-next-line react/jsx-pascal-case
    <SonnerToaster.component
      position="top-center"
      expand={false}
      toastOptions={{
        style: {
          right: 0,
        },
        classNames: {
          toast: 'pointer-events-auto flex items-center justify-center gap-2 rounded-2 w-full',
          title: 'typography-14',
        },
      }}
    />
  )
}

interface ToastProps {
  icon?: React.ReactNode
  title: string
  description: React.ReactNode
  handleClose: () => void
}

export function Toast({ icon, title, description, handleClose }: ToastProps) {
  return (
    <section className="bg-gray-1000 rounded-4 flex min-h-16 flex-col items-start justify-center border border-gray-900 p-4 shadow">
      <div className="flex w-[368px] items-center justify-start gap-4">
        <div className="rounded-2 flex items-start justify-start gap-2.5 bg-violet-500 text-service-white-white p-2 [&>svg]:size-6">
          {icon || <AlertTriangleIcon />}
        </div>
        <div className="flex w-[270px] flex-col items-start justify-center">
          <div className="typography-15 self-stretch pb-0.5 text-gray-50">{title}</div>
          <div className="typography-13 self-stretch text-gray-400">{description}</div>
        </div>
        <Button iconOnly size="xs" variant="ghost" onClick={handleClose} aria-label="Close toast">
          <XCloseIcon />
        </Button>
      </div>
    </section>
  )
}

// Sonner does not export this type so we need to redeclare it
type Position =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'bottom-center'

export async function toast({
  duration,
  position,
  id,
  ...props
}: Omit<ToastProps, 'handleClose'> & {
  duration?: number
  position?: Position
  // Reusing the same id updates the existing toast in place instead of stacking
  // a new one — used for progress that mutates rapidly (e.g. update downloads).
  id?: string | number
}) {
  const sonnerToast = await lazyToast()

  // https://sonner.emilkowal.ski/toast#render-toast-on-page-load
  // To render a toast on initial page load it is required that the function toast() is called inside of a setTimeout or requestAnimationFrame.
  // Since we are basically doing that, as we do a lazy load, we need to do the same thing.
  // oxlint-disable-next-line promise/avoid-new
  return new Promise<string | number>(resolve => {
    nextFrame(() => {
      const toastId = sonnerToast.custom(
        t => <Toast {...props} handleClose={() => sonnerToast.dismiss(t)} />,
        {
          duration: duration || 5000,
          ...(position && { position }),
          ...(id !== undefined && { id }),
        },
      )
      resolve(toastId)
    })
  })
}

export async function lazyToast() {
  const lazySonnerPromise = sonner()

  if (!useToastStore.getState().hasBeenCalled) {
    useToastStore.setState({ hasBeenCalled: true })
  }

  if (!sonnerPreloaded) {
    await SonnerToaster.preload()
    sonnerPreloaded = true
  }

  await toasterReady.promise

  const lazySonner = await lazySonnerPromise

  return lazySonner.toast
}
